import React, { useState } from 'react';
import Modal from 'react-bootstrap/lib/Modal';
import { useIntl } from 'react-intl';
import cx from 'classnames';
import messages from '../../locale/jsonMessages';
import { OPTION_LIMITS, DEFAULT_OPTION_SETTINGS, validateOptionInput } from '../../helpers/bookingOptionsBilling';
import { getOptionLabel, getBillingModeMessage, getOptionErrorMessage } from '../../helpers/bookingOptionsPresentation';
import { OptionIcon, CUSTOM_ICON_KEYS, ICON_GROUPS } from './optionIcons';

const ERROR_FIELD = {
  invalidOptionLabel: 'label', reservedOptionLabel: 'label', optionLabelTaken: 'label',
  invalidOptionDescription: 'description', invalidOptionPrice: 'price', invalidBillingMode: 'billingMode',
};

const cleanPrice = (value) => String(value).replace(/[^0-9.]/g, '');

// Add / edit form. Deliberately not a <form>: the modal is rendered inside the redux-form pricing step and a
// submit event would bubble (React portals) to its onSubmit.
function OptionEditModal({ option, rows, maxCustom, currency, styles: s, busy, serverError, onSave, onClose }) {
  const { formatMessage } = useIntl();
  const isNew = !option.slot;
  const isFixed = Boolean(option.isFixed);
  const [values, setValues] = useState({
    label: option.label || '',
    description: option.description || '',
    price: Number(option.price) > 0 ? String(option.price) : '',
    billingMode: option.billingMode || 'per_day',
    icon: option.icon || CUSTOM_ICON_KEYS[0],
    isActive: isNew ? true : (option.activate !== undefined ? Boolean(option.activate) : Boolean(option.isActive)),
  });
  const [errors, setErrors] = useState({});

  const set = (name, value) => setValues((current) => ({ ...current, [name]: value }));

  const submit = () => {
    const input = {
      slot: option.slot || undefined,
      label: isFixed ? null : values.label,
      description: isFixed ? null : values.description,
      price: values.price === '' ? 0 : Number(values.price),
      billingMode: isFixed ? option.billingMode : values.billingMode,
      icon: isFixed ? option.icon : values.icon,
      isActive: values.isActive,
    };
    const checked = validateOptionInput(input, rows, {
      ...DEFAULT_OPTION_SETTINGS, maxCustomOptions: Number.isInteger(maxCustom) ? maxCustom : DEFAULT_OPTION_SETTINGS.maxCustomOptions,
    });
    if (checked.error) {
      const field = ERROR_FIELD[checked.error] || 'general';
      setErrors({ [field]: getOptionErrorMessage(checked.error) });
      return;
    }
    setErrors({});
    onSave({ ...input, slot: checked.value.slot, label: checked.value.label, description: checked.value.description });
  };

  const onKeyDown = (event) => {
    if (event.key === 'Enter' && event.target.tagName === 'INPUT') {
      event.preventDefault();
      submit();
    }
  };

  const err = (field) => errors[field] && <span className={s.fieldError} role="alert">{formatMessage(errors[field])}</span>;
  const title = isNew ? messages.boAddOption : messages.boEdit;

  return (
    <Modal show onHide={onClose} dialogClassName="commonModalContainer">
      <Modal.Header className="commonModalHeader" closeButton>
        <Modal.Title className="commonModalTitle">
          {isFixed ? getOptionLabel(option, formatMessage) : formatMessage(title)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="commonModalBody">
        <div className={s.modalBody} onKeyDown={onKeyDown}>
          {!isFixed && (
            <>
              <div className={s.field}>
                <label htmlFor="bo-field-label">{formatMessage(messages.boFieldName)}</label>
                <input id="bo-field-label" type="text" className="commonInputField form-control" value={values.label}
                  maxLength={OPTION_LIMITS.labelMax} onChange={(e) => set('label', e.target.value)} />
                {err('label')}
              </div>
              <div className={s.field}>
                <label htmlFor="bo-field-description">{formatMessage(messages.boFieldDescription)}</label>
                <textarea id="bo-field-description" className="commonInputField form-control" rows={2}
                  value={values.description} maxLength={OPTION_LIMITS.descriptionMax}
                  onChange={(e) => set('description', e.target.value)} />
                <small className={s.counter}>{values.description.length} / {OPTION_LIMITS.descriptionMax}</small>
                {err('description')}
              </div>
              <div className={s.field}>
                <span className={s.fieldLabel}>{formatMessage(messages.boFieldIcon)}</span>
                <div className={s.iconPicker} role="radiogroup" aria-label={formatMessage(messages.boFieldIcon)}>
                  {ICON_GROUPS.map((group) => (
                    <div key={group.id} className={s.iconGroup}>
                      <span className={s.iconGroupTitle}>{formatMessage(messages['boIconGroup_' + group.id])}</span>
                      <div className={s.iconGroupItems}>
                        {group.keys.map((key) => (
                          <button type="button" key={key} role="radio" aria-checked={values.icon === key}
                            aria-label={key.replace(/_/g, ' ')} title={key.replace(/_/g, ' ')}
                            className={cx(s.iconChoice, values.icon === key && s.iconChoiceActive)}
                            onClick={() => set('icon', key)}>
                            <OptionIcon name={key} />
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className={s.field}>
                <label htmlFor="bo-field-billing">{formatMessage(messages.boFieldBilling)}</label>
                <select id="bo-field-billing" className="form-control" value={values.billingMode}
                  onChange={(e) => set('billingMode', e.target.value)}>
                  <option value="per_day">{formatMessage(getBillingModeMessage('per_day'))}</option>
                  <option value="flat">{formatMessage(getBillingModeMessage('flat'))}</option>
                </select>
                {err('billingMode')}
              </div>
            </>
          )}
          {isFixed && (
            <p className={s.fixedNote}>
              {formatMessage(messages.boFieldBilling)}: <strong>{formatMessage(getBillingModeMessage(option.billingMode))}</strong>
            </p>
          )}
          <div className={s.field}>
            <label htmlFor="bo-field-price">{formatMessage(messages.boFieldPrice)} ({currency})</label>
            <input id="bo-field-price" type="text" inputMode="decimal" className="commonInputField form-control"
              value={values.price} onChange={(e) => set('price', cleanPrice(e.target.value))} />
            {err('price')}
          </div>
          {!option.adminDisabled && (
            <label className={s.checkboxLine}>
              <input type="checkbox" checked={values.isActive} onChange={(e) => set('isActive', e.target.checked)} />
              {' '}{formatMessage(messages.boFieldActive)}
            </label>
          )}
          {err('general')}
          {serverError && <span className={s.fieldError} role="alert">{serverError}</span>}
        </div>
      </Modal.Body>
      <Modal.Footer className={s.modalFooter}>
        <button type="button" className={s.textButton} onClick={onClose} disabled={busy}>{formatMessage(messages.cancel)}</button>
        <button type="button" className={s.primaryButton} onClick={submit} disabled={busy}>{formatMessage(messages.save)}</button>
      </Modal.Footer>
    </Modal>
  );
}

export default OptionEditModal;
